import { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Button,
    Input,
    Label,
    Skeleton,
} from '@ui';
import {
    X,
    Edit2,
    Check,
    RotateCcw,
    Loader2,
    AlertCircle,
    History,
    Calendar,
    User,
    CreditCard,
    Coins,
    Package,
    Plus,
    Trash2,
    FileText,
    Calculator,
    Receipt,
    ChevronUp,
    ChevronDown,
    Search,
} from 'lucide-react';
import { cn } from '@ui/lib/utils';
import { toastMessage } from '@ui/components/Toast';
import { useGroupStore } from '@/stores/groupStore';
import { useGetScheduleActivityList, useUpdateActivity, useGetActivityTypeList, useCreateScheduleActivity } from '@hooks/generated/group';
import {
    useGetBillingDetail,
    useGetMemberVoucherList,
    useUpdateBilling,
} from '@hooks/generated/charge';
import { useQueryClient } from '@tanstack/react-query';
import type { PetActivityActivityResponse } from '@careforu-tailody/tailody-core-api';
import type { ApiBillingItem, PaymentMethod, MemberSubscription } from '../types';
import {
    getBillingStatusLabel,
    getBillingStatusStyle,
    getPaymentMethodLabel,
    formatAmount,
    formatDateTime,
} from '../utils';

interface BillingDetailModalProps {
    billing: ApiBillingItem;
    onClose: () => void;
    onUpdate?: (updatedBilling: ApiBillingItem) => void;
}

export function BillingDetailModal({
    billing: initialBilling,
    onClose,
    onUpdate,
}: BillingDetailModalProps) {
    const selectedGroup = useGroupStore((state) => state.selectedGroup);
    const groupId = selectedGroup?.id;
    const queryClient = useQueryClient();

    // 정산 상세 조회 API
    const { data: billingDetailData, isLoading: isLoadingDetail } = useGetBillingDetail({
        request: { groupId: groupId!, billingId: initialBilling.id },
        options: { enabled: !!groupId && !!initialBilling.id },
    });

    // API 데이터를 정산 상세에 병합
    const billing = useMemo(() => {
        if (!billingDetailData) return initialBilling;

        return {
            ...initialBilling,
            memberName: (billingDetailData as any).member?.name ?? initialBilling.memberName,
            memberPhone: (billingDetailData as any).member?.phone ?? initialBilling.memberPhone,
            schedulePetName: (billingDetailData as any).schedule?.petName ?? initialBilling.schedulePetName,
            baseAmount: billingDetailData.baseAmount ?? initialBilling.baseAmount,
            finalAmount: billingDetailData.finalAmount ?? initialBilling.finalAmount,
            calculatedAmount: billingDetailData.calculatedAmount ?? initialBilling.calculatedAmount,
            memo: billingDetailData.memo ?? initialBilling.memo,
            voucherUsages: billingDetailData.voucherUsages,
            activityItems: billingDetailData.activityItems,
            customItems: billingDetailData.customItems,
            discounts: billingDetailData.discounts,
            payments: billingDetailData.payments,
            cashRefunds: billingDetailData.cashRefunds,
            totalPaidAmount: billingDetailData.totalPaid,
            totalRefundedAmount: billingDetailData.totalRefunded,
            remainingAmount: billingDetailData.remainingAmount,
        };
    }, [billingDetailData, initialBilling]);

    // 일정의 활동 목록 조회
    const { data: activitiesData, refetch: refetchActivities } = useGetScheduleActivityList({
        request: { groupId: groupId!, scheduleId: billing.scheduleId! },
        options: { enabled: !!groupId && !!billing.scheduleId },
    });

    const scheduleActivities = (activitiesData || []) as PetActivityActivityResponse[];

    // 회원의 이용권 목록 조회 (실제 API)
    const { data: memberVouchersData } = useGetMemberVoucherList({
        request: {
            groupId: groupId!,
            memberId: String(billing.memberId || initialBilling.memberId || ''),
            showAll: true, // 모든 이용권 조회 (활성/비활성 포함)
        },
        options: { enabled: !!groupId && !!(billing.memberId || initialBilling.memberId) },
    });

    // API 뮤테이션 훅
    const updateBillingMutation = useUpdateBilling({
        options: {
            onSuccess: () => {
                toastMessage({ message: '정산이 수정되었습니다.', type: 'success' });
                queryClient.invalidateQueries({ queryKey: ['getBillingList'] });
                queryClient.invalidateQueries({ queryKey: ['getBillingDetail'] });
            },
            onError: () => {
                toastMessage({ message: '정산 수정에 실패했습니다.', type: 'error' });
            },
        },
    });

    // 활동 수정 API
    const updateActivityMutation = useUpdateActivity();

    // 활동 생성 API
    const createActivityMutation = useCreateScheduleActivity();

    // 지점 활동 타입 목록
    const { data: activityTypesData } = useGetActivityTypeList({
        request: { groupId: groupId! },
        options: { enabled: !!groupId },
    });

    const availableActivityTypes = useMemo(() => {
        if (activityTypesData && Array.isArray(activityTypesData)) {
            return (activityTypesData as Array<{ id?: number; name?: string; price?: number; isActive?: boolean }>)
                .filter(type => type.isActive !== false)
                .map(type => ({
                    id: type.id || Date.now(),
                    name: type.name || '',
                    defaultAmount: type.price || 0,
                }));
        }
        return [];
    }, [activityTypesData]);

    // 수정 모드 상태
    const [isEditMode, setIsEditMode] = useState(false);

    // 수정 가능한 필드 상태
    const [editBaseAmount, setEditBaseAmount] = useState((billing.baseAmount || 0).toString());
    const [editFinalAmount, setEditFinalAmount] = useState((billing.finalAmount || 0).toString());
    const [editPaymentMethod, setEditPaymentMethod] = useState<PaymentMethod | undefined>(billing.paymentMethod);
    // customItems를 editAdditionalItems 형식으로 변환
    const [editAdditionalItems, setEditAdditionalItems] = useState(() => {
        if (billing.customItems && billing.customItems.length > 0) {
            return billing.customItems.map((item, idx) => ({
                activityTypeId: item.id || idx,
                activityTypeName: item.name || '항목',
                defaultAmount: item.amount || 0,
                appliedAmount: (item.amount || 0) * (item.quantity || 1),
                included: true,
                autoCalculated: false,
            }));
        }
        return billing.additionalBillingItems || [];
    });
    const [editMemo, setEditMemo] = useState(billing.memo || '');

    // 활동 항목 상태 (정산에 포함할 활동)
    const [editActivityItems, setEditActivityItems] = useState<Array<{
        activityId: number;
        activityTypeId: number;
        activityTypeName: string;
        amount: number;
        included: boolean;
        startAt?: string;
        endAt?: string;
    }>>([]);

    // 활동 추가 상태 (기존 일정 활동 포함)
    const [showAddActivity, setShowAddActivity] = useState(false);
    // 새 활동 생성 폼 상태
    const [showNewActivityForm, setShowNewActivityForm] = useState(false);
    const [newActivitySearch, setNewActivitySearch] = useState('');
    const [newActivityTypeId, setNewActivityTypeId] = useState<number | null>(null);
    const [newActivityTypeName, setNewActivityTypeName] = useState('');
    const [newActivityStatus, setNewActivityStatus] = useState<'SCHEDULED' | 'PROGRESS' | 'COMPLETED' | 'CANCELED'>('COMPLETED');
    const [newActivityStartAt, setNewActivityStartAt] = useState('');
    const [newActivityEndAt, setNewActivityEndAt] = useState('');
    const [newActivityInstantComplete, setNewActivityInstantComplete] = useState(false);

    // 활동 시간 수정 상태
    const [editingActivityTimeId, setEditingActivityTimeId] = useState<number | null>(null);
    const [editingActivityStatus, setEditingActivityStatus] = useState<string>('COMPLETED');
    const [editingActivityStartAt, setEditingActivityStartAt] = useState('');
    const [editingActivityEndAt, setEditingActivityEndAt] = useState('');
    const [editingActivityInstantComplete, setEditingActivityInstantComplete] = useState(false);

    // 추가 가능한 활동 목록 (이미 포함된 활동 제외)
    const availableActivities = useMemo(() => {
        const existingActivityIds = new Set(editActivityItems.map(item => item.activityId));
        return scheduleActivities.filter(activity => !existingActivityIds.has(activity.id || 0));
    }, [scheduleActivities, editActivityItems]);

    // 추가 결제 항목 수정 상태
    const [editingItemId, setEditingItemId] = useState<number | null>(null);
    const [editItemName, setEditItemName] = useState('');
    const [editItemAmount, setEditItemAmount] = useState('');
    const [showAddItem, setShowAddItem] = useState(false);
    const [newItemName, setNewItemName] = useState('');
    const [newItemAmount, setNewItemAmount] = useState('');

    // 할인 항목 상태 - API discounts를 사용
    const [discountItems, setDiscountItems] = useState<Array<{
        voucherId: number;
        voucherName: string;
        targetActivityTypeId?: number;
        discountAmount: number;
    }>>(() => {
        if (billing.discounts && billing.discounts.length > 0) {
            return billing.discounts.map((d, idx) => ({
                voucherId: d.id || idx,
                voucherName: d.name || '할인',
                discountAmount: d.amount || 0,
            }));
        }
        return billing.appliedDiscountVouchers || [];
    });
    const [showAddDiscount, setShowAddDiscount] = useState(false);
    const [newDiscountName, setNewDiscountName] = useState('');
    const [newDiscountAmount, setNewDiscountAmount] = useState('');

    // 이용권 선택 상태 - API voucherUsages를 사용
    const [selectedSubscriptions, setSelectedSubscriptions] = useState<Array<{
        subscriptionId: number;
        deductedCount: number;
        settlementAmount: number;
    }>>(() => {
        // 새 API의 voucherUsages를 먼저 확인
        if (billing.voucherUsages && billing.voucherUsages.length > 0) {
            return billing.voucherUsages.map(v => ({
                subscriptionId: v.memberVoucherId || 0,
                deductedCount: v.usedCount || 1,
                settlementAmount: v.settlementAmount || 0,
            }));
        }
        // 기존 방식 폴백
        if (billing.subscriptionId) {
            return [{
                subscriptionId: billing.subscriptionId,
                deductedCount: billing.deductedCount || 1,
                settlementAmount: billing.voucherSettlementAmount || 0,
            }];
        }
        return [];
    });

    // 부분결제/환불 상태 - API payments, cashRefunds를 사용
    const [partialPayments, setPartialPayments] = useState(() => {
        // 새 API의 payments를 먼저 확인
        if (billing.payments && billing.payments.length > 0) {
            return billing.payments.map((p, idx) => ({
                id: p.id || idx,
                amount: p.amount || 0,
                paymentMethod: (p.method || 'CASH') as PaymentMethod,
                paidAt: p.paidAt || new Date().toISOString(),
                memo: '',
            }));
        }
        return billing.partialPayments || [];
    });
    const [partialRefunds, setPartialRefunds] = useState(() => {
        // 새 API의 cashRefunds를 먼저 확인 (BillingCashRefundResponse uses 'method' and 'amount')
        if (billing.cashRefunds && billing.cashRefunds.length > 0) {
            return billing.cashRefunds.map((r, idx) => ({
                id: r.id || idx,
                paymentId: 0,
                amount: r.amount || 0,  // BillingCashRefundResponse uses 'amount'
                refundMethod: (r.method || 'CASH') as PaymentMethod,  // BillingCashRefundResponse uses 'method'
                refundedAt: r.refundedAt || new Date().toISOString(),
                reason: r.reason || '',
            }));
        }
        // payments의 refunds도 확인
        if (billing.payments && billing.payments.length > 0) {
            const allRefunds: Array<{
                id: number;
                paymentId: number;
                amount: number;
                refundMethod: PaymentMethod;
                refundedAt: string;
                reason: string;
            }> = [];
            billing.payments.forEach((p, pIdx) => {
                if (p.refunds && p.refunds.length > 0) {
                    p.refunds.forEach((r, rIdx) => {
                        allRefunds.push({
                            id: r.id || (pIdx * 100 + rIdx),
                            paymentId: p.id || pIdx,
                            amount: r.refundAmount || 0,
                            refundMethod: (r.refundMethod || 'CASH') as PaymentMethod,
                            refundedAt: r.refundedAt || new Date().toISOString(),
                            reason: r.reason || '',
                        });
                    });
                }
            });
            if (allRefunds.length > 0) return allRefunds;
        }
        return billing.partialRefunds || [];
    });
    const [showPaymentHistory, setShowPaymentHistory] = useState(false);
    const [showAddPayment, setShowAddPayment] = useState(false);
    const [showAddRefund, setShowAddRefund] = useState(false);
    const [newPaymentAmount, setNewPaymentAmount] = useState('');
    const [newPaymentMethod, setNewPaymentMethod] = useState<PaymentMethod>('CARD');
    const [newPaymentMemo, setNewPaymentMemo] = useState('');
    const [newRefundAmount, setNewRefundAmount] = useState('');
    const [newRefundMethod, setNewRefundMethod] = useState<PaymentMethod>('CARD');
    const [newRefundReason, setNewRefundReason] = useState('');

    const statusStyle = getBillingStatusStyle(billing.status);

    // 회원 구독 목록 (횟수제, 선불제만) - API 데이터 사용
    const memberSubscriptions = useMemo(() => {
        if (memberVouchersData && memberVouchersData.length > 0) {
            // API MemberVoucherResponse를 MemberSubscription 형식으로 매핑
            return memberVouchersData
                .filter(v => v.status === 'ACTIVE' && v.snapshotType !== 'HOURLY')
                .map(v => ({
                    id: v.id || 0,
                    memberId: billing.memberId || 0,
                    pricingPlanId: v.id || 0,
                    pricingPlanName: v.snapshotName || '이용권',
                    pricingPlanType: v.snapshotType || 'PACKAGE',
                    remainingCount: v.remainingCount,
                    totalCount: v.snapshotTotalCount,
                    remainingAmount: v.remainingBalance,
                    totalAmount: v.snapshotTotalAmount,
                    hourlyRate: v.snapshotHourlyRate,
                    minimumMinutes: v.snapshotMinimumMinutes,
                    status: v.status || 'ACTIVE',
                    expiresAt: undefined,
                }));
        }
        // API 데이터가 없으면 빈 배열 반환
        return [];
    }, [memberVouchersData, billing.memberId]);

    // 활동 항목 초기화 (billing.activityItems 또는 scheduleActivities 로드 시)
    useEffect(() => {
        if (editActivityItems.length === 0) {
            // API의 activityItems가 있으면 우선 사용
            if (billing.activityItems && billing.activityItems.length > 0) {
                const activityItems = billing.activityItems.map((item, idx) => {
                    const matchedActivity = scheduleActivities.find(a => a.id === (item.petActivityId || item.id));
                    return {
                        activityId: item.petActivityId || item.id || idx,
                        activityTypeId: item.id || idx,
                        activityTypeName: item.snapshotActivityTypeName || '활동',
                        amount: item.appliedAmount || item.snapshotDefaultAmount || 0,
                        included: item.included !== false,
                        startAt: matchedActivity?.startAt,
                        endAt: matchedActivity?.endAt,
                    };
                });
                setEditActivityItems(activityItems);
            } else if (scheduleActivities.length > 0) {
                // 폴백: scheduleActivities 사용
                const activityItems = scheduleActivities.map(activity => ({
                    activityId: activity.id || 0,
                    activityTypeId: activity.activityTypeInfo?.id || 0,
                    activityTypeName: activity.activityTypeInfo?.name || '활동',
                    amount: activity.activityTypeInfo?.defaultAmount || 0,
                    included: activity.status === 'COMPLETED',
                    startAt: activity.startAt,
                    endAt: activity.endAt,
                }));
                setEditActivityItems(activityItems);
            }
        }
    }, [scheduleActivities, billing.activityItems, editActivityItems.length]);

    // 활동 항목 합계 계산
    const activityItemTotal = useMemo(() => {
        return editActivityItems
            .filter(item => item.included)
            .reduce((sum, item) => sum + item.amount, 0);
    }, [editActivityItems]);

    // 추가 결제 항목 합계 계산
    const additionalItemTotal = useMemo(() => {
        return editAdditionalItems
            .filter(item => item.included)
            .reduce((sum, item) => sum + item.appliedAmount, 0);
    }, [editAdditionalItems]);

    // 할인 합계 계산
    const totalDiscount = useMemo(() => {
        return discountItems.reduce((sum, item) => sum + item.discountAmount, 0);
    }, [discountItems]);

    // 이용권 정산금 합계 계산
    const totalVoucherSettlement = useMemo(() => {
        return selectedSubscriptions.reduce((sum, sel) => sum + sel.settlementAmount, 0);
    }, [selectedSubscriptions]);

    // 자동 계산 금액
    const autoCalculatedAmount = useMemo(() => {
        const base = parseInt(editBaseAmount) || 0;
        return Math.max(0, base + activityItemTotal + additionalItemTotal - totalDiscount - totalVoucherSettlement);
    }, [editBaseAmount, activityItemTotal, additionalItemTotal, totalDiscount, totalVoucherSettlement]);

    // 부분결제/환불 합계
    const totalPaidAmount = useMemo(() => {
        return partialPayments.reduce((sum, p) => sum + p.amount, 0);
    }, [partialPayments]);

    const totalRefundedAmount = useMemo(() => {
        return partialRefunds.reduce((sum, r) => sum + r.amount, 0);
    }, [partialRefunds]);

    const finalAmountValue = parseInt(editFinalAmount) || autoCalculatedAmount;
    const remainingPaymentAmount = Math.max(0, finalAmountValue - totalPaidAmount);
    const refundableAmount = Math.max(0, totalPaidAmount - totalRefundedAmount);

    // 검색 필터된 활동 타입 목록
    const filteredActivityTypes = useMemo(() => {
        if (!newActivitySearch.trim()) return availableActivityTypes;
        return availableActivityTypes.filter(t =>
            t.name.toLowerCase().includes(newActivitySearch.trim().toLowerCase())
        );
    }, [availableActivityTypes, newActivitySearch]);

    // 활동 추가 폼 초기화
    const resetNewActivityForm = () => {
        setNewActivitySearch('');
        setNewActivityTypeId(null);
        setNewActivityTypeName('');
        setNewActivityStatus('COMPLETED');
        setNewActivityStartAt('');
        setNewActivityEndAt('');
        setNewActivityInstantComplete(false);
        setShowAddActivity(false);
    };

    // 활동 추가 저장 (생성 API 호출)
    const handleAddNewActivity = async () => {
        if (!newActivityTypeId) {
            toastMessage({ message: '활동을 선택해주세요.', type: 'warning' });
            return;
        }
        if (!newActivityStartAt) {
            toastMessage({ message: '시작일시를 입력해주세요.', type: 'warning' });
            return;
        }

        try {
            const createResponse = await createActivityMutation.mutateAsync({
                groupId: groupId!,
                scheduleId: billing.scheduleId!,
                petActivityScheduleActivityCreateRequest: {
                    activityTypeId: newActivityTypeId,
                    startAt: convertToUTC(newActivityStartAt),
                    endAt: newActivityInstantComplete ? convertToUTC(newActivityStartAt) : (newActivityEndAt ? convertToUTC(newActivityEndAt) : undefined),
                },
            });

            // 생성 후 상태가 기본(SCHEDULED)이 아닌 경우 업데이트
            const createdActivityId = (createResponse?.data as any)?.data?.id;
            if (createdActivityId && newActivityStatus !== 'SCHEDULED') {
                await updateActivityMutation.mutateAsync({
                    groupId: groupId!,
                    activityId: createdActivityId,
                    petActivityActivityUpdateRequest: {
                        status: newActivityStatus as any,
                        startAt: convertToUTC(newActivityStartAt),
                        endAt: newActivityInstantComplete ? convertToUTC(newActivityStartAt) : (newActivityEndAt ? convertToUTC(newActivityEndAt) : undefined),
                    },
                });
            }

            await refetchActivities();

            // 로컬 editActivityItems에도 추가
            const matchedType = availableActivityTypes.find(t => t.id === newActivityTypeId);
            const newItem = {
                activityId: createdActivityId || Date.now(),
                activityTypeId: newActivityTypeId,
                activityTypeName: newActivityTypeName,
                amount: matchedType?.defaultAmount || 0,
                included: true,
                startAt: convertToUTC(newActivityStartAt),
                endAt: newActivityInstantComplete ? convertToUTC(newActivityStartAt) : (newActivityEndAt ? convertToUTC(newActivityEndAt) : undefined),
            };
            setEditActivityItems(prev => [...prev, newItem]);

            toastMessage({ message: `'${newActivityTypeName}' 활동이 추가되었습니다.`, type: 'success' });
            resetNewActivityForm();
        } catch (error) {
            console.error('활동 추가 실패:', error);
            toastMessage({ message: '활동 추가에 실패했습니다.', type: 'error' });
        }
    };

    // 활동 삭제 핸들러
    const handleRemoveActivity = (activityId: number) => {
        setEditActivityItems(prev => prev.filter(item => item.activityId !== activityId));
    };

    // 일정의 기존 활동을 정산 항목에 추가
    const handleAddActivity = (activity: typeof scheduleActivities[0]) => {
        const newItem = {
            activityId: activity.id || 0,
            activityTypeId: activity.activityTypeInfo?.id || 0,
            activityTypeName: activity.activityTypeInfo?.name || '활동',
            amount: activity.activityTypeInfo?.defaultAmount || 0,
            included: true,
            startAt: activity.startAt,
            endAt: activity.endAt,
        };
        setEditActivityItems(prev => [...prev, newItem]);
    };

    // UTC → KST 시간 표시 (+9시간)
    const formatActivityTime = (dateString?: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        // UTC → KST (+9시간)
        const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
        return kstDate.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    };

    // datetime-local 형식으로 변환 (UTC → KST +9시간)
    const formatDateTimeLocal = (dateStr?: string) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);
        return kstDate.toISOString().slice(0, 16);
    };

    // KST datetime-local 문자열을 UTC ISO 문자열로 변환 (브라우저 timezone 의존하지 않음)
    const convertToUTC = (dateTimeLocal: string): string => {
        if (!dateTimeLocal) return new Date().toISOString();
        const [datePart, timePart] = dateTimeLocal.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
        // KST 시간을 UTC로 취급하여 타임스탬프 생성 후 9시간 빼기
        const kstAsUTC = Date.UTC(year, month - 1, day, hours, minutes, 0);
        const utcDate = new Date(kstAsUTC - 9 * 60 * 60 * 1000);
        return utcDate.toISOString();
    };

    // 활동 시간 수정 시작
    const handleStartEditActivityTime = (item: typeof editActivityItems[0]) => {
        const matchedActivity = scheduleActivities.find(a => a.id === item.activityId);
        setEditingActivityTimeId(item.activityId);
        setEditingActivityStatus((matchedActivity?.status as string) || 'COMPLETED');
        const startAt = item.startAt || matchedActivity?.startAt;
        const endAt = item.endAt || matchedActivity?.endAt;
        const isInstant = !endAt
            || (startAt && endAt && new Date(startAt).getTime() === new Date(endAt).getTime());
        setEditingActivityStartAt(formatDateTimeLocal(startAt));
        setEditingActivityEndAt(isInstant ? '' : formatDateTimeLocal(endAt));
        setEditingActivityInstantComplete(!!isInstant);
    };

    // 활동 시간 수정 취소
    const handleCancelEditActivityTime = () => {
        setEditingActivityTimeId(null);
        setEditingActivityStatus('COMPLETED');
        setEditingActivityStartAt('');
        setEditingActivityEndAt('');
        setEditingActivityInstantComplete(false);
    };

    // 활동 시간 수정 저장
    const handleSaveEditActivityTime = async () => {


        if (!editingActivityTimeId || !groupId) return;
        try {
            await updateActivityMutation.mutateAsync({
                groupId: groupId,
                activityId: editingActivityTimeId,
                petActivityActivityUpdateRequest: {
                    status: editingActivityStatus as any,
                    startAt: convertToUTC(editingActivityStartAt),
                    endAt: editingActivityInstantComplete ? convertToUTC(editingActivityStartAt) : (editingActivityEndAt ? convertToUTC(editingActivityEndAt) : undefined),
                },
            });
            await refetchActivities();
            // 로컬 상태 업데이트
            setEditActivityItems(prev => prev.map(item =>
                item.activityId === editingActivityTimeId
                    ? {
                        ...item,
                        startAt: editingActivityStartAt ? convertToUTC(editingActivityStartAt) : item.startAt,
                        endAt: editingActivityInstantComplete ? convertToUTC(editingActivityStartAt) : (editingActivityEndAt ? convertToUTC(editingActivityEndAt) : item.endAt),
                    }
                    : item
            ));
            toastMessage({ message: '활동 시간이 수정되었습니다.', type: 'success' });
            handleCancelEditActivityTime();
        } catch (error) {
            console.error('활동 시간 수정 실패:', error);
            toastMessage({ message: '활동 시간 수정에 실패했습니다.', type: 'error' });
        }
    };

    // 수정 모드 시작
    const handleStartEdit = () => {
        setIsEditMode(true);
    };

    // 수정 취소
    const handleCancelEdit = () => {
        // 원래 값으로 복원
        setEditBaseAmount((billing.baseAmount || 0).toString());
        setEditFinalAmount((billing.finalAmount || 0).toString());
        setEditPaymentMethod(billing.paymentMethod);
        setEditMemo(billing.memo || '');

        // customItems로 editAdditionalItems 복원
        if (billing.customItems && billing.customItems.length > 0) {
            setEditAdditionalItems(billing.customItems.map((item, idx) => ({
                activityTypeId: item.id || idx,
                activityTypeName: item.name || '항목',
                defaultAmount: item.amount || 0,
                appliedAmount: (item.amount || 0) * (item.quantity || 1),
                included: true,
                autoCalculated: false,
            })));
        } else {
            setEditAdditionalItems(billing.additionalBillingItems || []);
        }

        // activityItems로 editActivityItems 복원
        if (billing.activityItems && billing.activityItems.length > 0) {
            setEditActivityItems(billing.activityItems.map((item, idx) => {
                const matchedActivity = scheduleActivities.find(a => a.id === (item.petActivityId || item.id));
                return {
                    activityId: item.petActivityId || item.id || idx,
                    activityTypeId: item.id || idx,
                    activityTypeName: item.snapshotActivityTypeName || '활동',
                    amount: item.appliedAmount || item.snapshotDefaultAmount || 0,
                    included: item.included !== false,
                    startAt: matchedActivity?.startAt,
                    endAt: matchedActivity?.endAt,
                };
            }));
        } else {
            setEditActivityItems(scheduleActivities.map(activity => ({
                activityId: activity.id || 0,
                activityTypeId: activity.activityTypeInfo?.id || 0,
                activityTypeName: activity.activityTypeInfo?.name || '활동',
                amount: activity.activityTypeInfo?.defaultAmount || 0,
                included: activity.status === 'COMPLETED',
                startAt: activity.startAt,
                endAt: activity.endAt,
            })));
        }

        // discounts로 discountItems 복원
        if (billing.discounts && billing.discounts.length > 0) {
            setDiscountItems(billing.discounts.map((d, idx) => ({
                voucherId: d.id || idx,
                voucherName: d.name || '할인',
                discountAmount: d.amount || 0,
            })));
        } else {
            setDiscountItems(billing.appliedDiscountVouchers || []);
        }

        // voucherUsages로 selectedSubscriptions 복원
        if (billing.voucherUsages && billing.voucherUsages.length > 0) {
            setSelectedSubscriptions(billing.voucherUsages.map(v => ({
                subscriptionId: v.memberVoucherId || 0,
                deductedCount: v.usedCount || 1,
                settlementAmount: v.settlementAmount || 0,
            })));
        } else if (billing.subscriptionId) {
            setSelectedSubscriptions([{
                subscriptionId: billing.subscriptionId,
                deductedCount: billing.deductedCount || 1,
                settlementAmount: billing.voucherSettlementAmount || 0,
            }]);
        } else {
            setSelectedSubscriptions([]);
        }

        // payments로 partialPayments 복원
        if (billing.payments && billing.payments.length > 0) {
            setPartialPayments(billing.payments.map((p, idx) => ({
                id: p.id || idx,
                amount: p.amount || 0,
                paymentMethod: (p.method || 'CASH') as PaymentMethod,
                paidAt: p.paidAt || new Date().toISOString(),
                memo: '',
            })));
        } else {
            setPartialPayments(billing.partialPayments || []);
        }

        // cashRefunds 또는 payments.refunds로 partialRefunds 복원
        // Note: cashRefunds uses 'method' and 'amount', while payments.refunds uses 'refundMethod' and 'refundAmount'
        if (billing.cashRefunds && billing.cashRefunds.length > 0) {
            setPartialRefunds(billing.cashRefunds.map((r, idx) => ({
                id: r.id || idx,
                paymentId: 0,
                amount: r.amount || 0,  // BillingCashRefundResponse uses 'amount'
                refundMethod: (r.method || 'CASH') as PaymentMethod,  // BillingCashRefundResponse uses 'method'
                refundedAt: r.refundedAt || new Date().toISOString(),
                reason: r.reason || '',
            })));
        } else if (billing.payments) {
            const allRefunds: typeof partialRefunds = [];
            billing.payments.forEach((p, pIdx) => {
                if (p.refunds && p.refunds.length > 0) {
                    p.refunds.forEach((r, rIdx) => {
                        allRefunds.push({
                            id: r.id || (pIdx * 100 + rIdx),
                            paymentId: p.id || pIdx,
                            amount: r.refundAmount || 0,
                            refundMethod: (r.refundMethod || 'CASH') as PaymentMethod,
                            refundedAt: r.refundedAt || new Date().toISOString(),
                            reason: r.reason || '',
                        });
                    });
                }
            });
            if (allRefunds.length > 0) {
                setPartialRefunds(allRefunds);
            } else {
                setPartialRefunds(billing.partialRefunds || []);
            }
        } else {
            setPartialRefunds(billing.partialRefunds || []);
        }

        setShowAddActivity(false);
        handleCancelEditActivityTime();
        setIsEditMode(false);
    };

    // 수정 저장
    const handleSaveEdit = async () => {
        // API를 통해 정산 수정 - BillingUpdateRequest 사용
        try {
            // 활동 항목 변환 (포함된 항목만)
            const activityItems = editActivityItems
                .filter(item => item.included)
                .map(item => ({
                    petActivityId: item.activityId,
                    included: true,
                    appliedAmount: item.amount,
                }));

            // 커스텀 항목 변환
            const customItems = editAdditionalItems.map(item => ({
                name: item.activityTypeName,
                amount: item.appliedAmount,
                quantity: 1,
            }));

            // 할인 항목 변환
            const discounts = discountItems.map(d => ({
                name: d.voucherName,
                amount: d.discountAmount,
            }));

            // 이용권 사용 내역 변환
            const voucherUsages = selectedSubscriptions.map(sel => ({
                memberVoucherId: sel.subscriptionId,
                usedCount: sel.deductedCount,
                amount: sel.settlementAmount,
            }));

            // 결제 정보 변환 (cashPayments)
            const cashPayments = partialPayments.map(p => ({
                method: p.paymentMethod as 'CASH' | 'CARD' | 'TRANSFER' | 'OTHER',
                amount: p.amount,
                paidAt: p.paidAt,
                memo: p.memo,
            }));

            // 환불 정보 변환 (cashRefunds)
            const cashRefunds = partialRefunds.map(r => ({
                method: r.refundMethod as 'CASH' | 'CARD' | 'TRANSFER' | 'OTHER',
                amount: r.amount,
                refundedAt: r.refundedAt,
                reason: r.reason,
            }));

            // 금액 차이 계산
            const baseAmount = parseInt(editBaseAmount) || 0;
            const amountDiff = finalAmountValue - autoCalculatedAmount;

            await updateBillingMutation.mutateAsync({
                groupId: groupId!,
                billingId: billing.id,
                billingUpdateRequest: {
                    baseAmount: baseAmount,
                    activityItems: activityItems.length > 0 ? activityItems : undefined,
                    customItems: customItems.length > 0 ? customItems : undefined,
                    discounts: discounts.length > 0 ? discounts : undefined,
                    voucherUsages: voucherUsages.length > 0 ? voucherUsages : undefined,
                    cashPayments: cashPayments.length > 0 ? cashPayments : undefined,
                    cashRefunds: cashRefunds.length > 0 ? cashRefunds : undefined,
                    finalAmount: finalAmountValue,
                    finalAmountReason: amountDiff !== 0
                        ? `계산 금액(${autoCalculatedAmount}원)과 ${Math.abs(amountDiff)}원 차이`
                        : undefined,
                    memo: editMemo.trim() || undefined,
                },
            });

            // 로컬 상태 업데이트
            const updatedBilling: ApiBillingItem = {
                ...billing,
                baseAmount: parseInt(editBaseAmount) || 0,
                finalAmount: finalAmountValue,
                paymentMethod: editPaymentMethod,
                additionalBillingItems: editAdditionalItems,
                additionalBillingItemTotal: additionalItemTotal,
                appliedDiscountVouchers: discountItems,
                discountVoucherTotal: totalDiscount,
                subscriptionId: selectedSubscriptions[0]?.subscriptionId,
                deductedCount: selectedSubscriptions[0]?.deductedCount,
                voucherSettlementAmount: totalVoucherSettlement,
                calculatedAmount: autoCalculatedAmount,
                partialPayments,
                partialRefunds,
                totalPaidAmount,
                totalRefundedAmount,
                memo: editMemo.trim() || undefined,
            };

            if (onUpdate) {
                onUpdate(updatedBilling);
            }

            setShowAddActivity(false);
            handleCancelEditActivityTime();
            setIsEditMode(false);
        } catch (error) {
            console.error('Failed to update billing:', error);
        }
    };

    // 추가 결제 항목 수정 시작
    const handleStartEditItem = (item: typeof editAdditionalItems[0]) => {
        setEditingItemId(item.activityTypeId);
        setEditItemName(item.activityTypeName);
        setEditItemAmount(item.appliedAmount.toString());
    };

    // 추가 결제 항목 수정 저장
    const handleSaveEditItem = () => {
        if (!editItemName.trim()) {
            toastMessage({ message: '항목명을 입력해주세요.', type: 'warning' });
            return;
        }
        setEditAdditionalItems(prev =>
            prev.map(item =>
                item.activityTypeId === editingItemId
                    ? { ...item, activityTypeName: editItemName.trim(), appliedAmount: parseInt(editItemAmount) || 0 }
                    : item
            )
        );
        setEditingItemId(null);
        setEditItemName('');
        setEditItemAmount('');
    };

    // 추가 결제 항목 수정 취소
    const handleCancelEditItem = () => {
        setEditingItemId(null);
        setEditItemName('');
        setEditItemAmount('');
    };

    // 추가 결제 항목 삭제
    const handleRemoveItem = (activityTypeId: number) => {
        setEditAdditionalItems(prev => prev.filter(item => item.activityTypeId !== activityTypeId));
    };

    // 새 추가 결제 항목 추가 (로컬 상태에 추가, 저장 시 API 호출)
    const handleAddNewItem = () => {
        if (!newItemName.trim()) {
            toastMessage({ message: '항목명을 입력해주세요.', type: 'warning' });
            return;
        }
        const amount = parseInt(newItemAmount) || 0;

        // 로컬 상태 업데이트 (저장 버튼 클릭 시 API 호출됨)
        const newItem = {
            activityTypeId: Date.now(),
            activityTypeName: newItemName.trim(),
            defaultAmount: amount,
            appliedAmount: amount,
            included: true,
            autoCalculated: false,
        };
        setEditAdditionalItems(prev => [...prev, newItem]);
        setNewItemName('');
        setNewItemAmount('');
        setShowAddItem(false);
        toastMessage({ message: '항목이 추가되었습니다. 저장 버튼을 눌러 적용하세요.', type: 'success' });
    };

    // 할인 항목 추가 (로컬 상태에 추가, 저장 시 API 호출)
    const handleAddDiscount = () => {
        if (!newDiscountName.trim()) {
            toastMessage({ message: '할인명을 입력해주세요.', type: 'warning' });
            return;
        }
        const amount = parseInt(newDiscountAmount) || 0;
        if (amount <= 0) {
            toastMessage({ message: '할인 금액을 입력해주세요.', type: 'warning' });
            return;
        }

        // 로컬 상태 업데이트 (저장 버튼 클릭 시 API 호출됨)
        const newDiscount = {
            voucherId: Date.now(),
            voucherName: newDiscountName.trim(),
            discountAmount: amount,
        };
        setDiscountItems(prev => [...prev, newDiscount]);
        setNewDiscountName('');
        setNewDiscountAmount('');
        setShowAddDiscount(false);
        toastMessage({ message: '할인이 추가되었습니다. 저장 버튼을 눌러 적용하세요.', type: 'success' });
    };

    // 할인 항목 삭제
    const handleRemoveDiscount = (voucherId: number) => {
        setDiscountItems(prev => prev.filter(d => d.voucherId !== voucherId));
    };

    // 이용권 추가
    const handleAddSubscription = (subscriptionId: number) => {
        const sub = memberSubscriptions.find(s => s.id === subscriptionId);
        if (!sub) return;

        setSelectedSubscriptions(prev => [...prev, {
            subscriptionId,
            deductedCount: 1,
            settlementAmount: 0,
        }]);
    };

    // 이용권 제거
    const handleRemoveSubscription = (subscriptionId: number) => {
        setSelectedSubscriptions(prev => prev.filter(s => s.subscriptionId !== subscriptionId));
    };

    // 이용권 차감 횟수 수정
    const handleUpdateDeductedCount = (subscriptionId: number, count: number) => {
        setSelectedSubscriptions(prev =>
            prev.map(s =>
                s.subscriptionId === subscriptionId
                    ? { ...s, deductedCount: count }
                    : s
            )
        );
    };

    // 이용권 정산금 수정
    const handleUpdateSettlementAmount = (subscriptionId: number, amount: number) => {
        setSelectedSubscriptions(prev =>
            prev.map(s =>
                s.subscriptionId === subscriptionId
                    ? { ...s, settlementAmount: amount }
                    : s
            )
        );
    };

    // 부분결제 추가 (로컬 상태, 저장시 API에 포함)
    const handleAddPartialPayment = () => {
        const amount = parseInt(newPaymentAmount) || 0;
        if (amount <= 0) {
            toastMessage({ message: '결제 금액을 입력해주세요.', type: 'warning' });
            return;
        }
        if (amount > remainingPaymentAmount) {
            toastMessage({ message: '남은 결제 금액보다 큰 금액은 입력할 수 없습니다.', type: 'warning' });
            return;
        }
        if (newPaymentMethod === 'OTHER' && !newPaymentMemo.trim()) {
            toastMessage({ message: '기타 결제 방법의 경우 상세 내용을 입력해주세요.', type: 'warning' });
            return;
        }

        // 로컬 상태 업데이트 (저장 버튼 클릭 시 API 호출됨)
        const newPayment = {
            id: Date.now(),
            amount,
            paymentMethod: newPaymentMethod,
            memo: newPaymentMemo.trim() || undefined,
            paidAt: new Date().toISOString(),
        };
        setPartialPayments(prev => [...prev, newPayment]);
        setNewPaymentAmount('');
        setNewPaymentMemo('');
        setShowAddPayment(false);
        toastMessage({ message: '결제가 추가되었습니다. 저장 버튼을 눌러 적용하세요.', type: 'success' });
    };

    // 부분결제 삭제
    const handleRemovePartialPayment = (id: number) => {
        setPartialPayments(prev => prev.filter(p => p.id !== id));
    };

    // 부분환불 추가 (로컬 상태, 저장시 API에 포함)
    const handleAddPartialRefund = () => {
        const amount = parseInt(newRefundAmount) || 0;
        if (amount <= 0) {
            toastMessage({ message: '환불 금액을 입력해주세요.', type: 'warning' });
            return;
        }
        if (amount > refundableAmount) {
            toastMessage({ message: '환불 가능 금액보다 큰 금액은 입력할 수 없습니다.', type: 'warning' });
            return;
        }
        if (!newRefundReason.trim()) {
            toastMessage({ message: '환불 사유를 입력해주세요.', type: 'warning' });
            return;
        }

        // 환불할 결제 ID 찾기 (가장 최근 결제 사용)
        const latestPayment = partialPayments[partialPayments.length - 1];
        const paymentId = latestPayment?.id || 0;

        // 로컬 상태 업데이트 (저장 버튼 클릭 시 API 호출됨)
        const newRefund = {
            id: Date.now(),
            paymentId: paymentId,
            amount,
            refundMethod: newRefundMethod,
            reason: newRefundReason.trim(),
            refundedAt: new Date().toISOString(),
        };
        setPartialRefunds(prev => [...prev, newRefund]);
        setNewRefundAmount('');
        setNewRefundReason('');
        setShowAddRefund(false);
        toastMessage({ message: '환불이 추가되었습니다. 저장 버튼을 눌러 적용하세요.', type: 'success' });
    };

    // 부분환불 삭제
    const handleRemovePartialRefund = (id: number) => {
        setPartialRefunds(prev => prev.filter(r => r.id !== id));
    };

    // 결제 방법 옵션
    const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
        { value: 'CARD', label: '카드' },
        { value: 'CASH', label: '현금' },
        { value: 'TRANSFER', label: '계좌이체' },
        { value: 'OTHER', label: '기타' },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* 헤더 - 단순화 */}
                <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b dark:border-neutral-700 px-4 py-3 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">정산 상세</h3>
                        <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 font-mono">#{billing.id}</span>
                        <span className={cn(
                            "text-xs px-2 py-0.5 rounded",
                            statusStyle.bg,
                            statusStyle.text
                        )}>
                            {getBillingStatusLabel(billing.status)}
                        </span>
                        {isEditMode && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                수정중
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditMode && (
                            <Button variant="outline" size="sm" onClick={handleStartEdit}>
                                수정
                            </Button>
                        )}
                        <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800 rounded">
                            <X className="h-5 w-5 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                        </button>
                    </div>
                </div>

                {/* 내용 */}
                <div>
                    {isLoadingDetail ? (
                        <div className="flex items-center justify-center py-12 gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                            <span className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">불러오는 중...</span>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {/* 기본 정보 */}
                            <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">고객</span>
                                        <span className="font-medium">{billing.memberName || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">연락처</span>
                                        <span>{billing.memberPhone || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">반려동물</span>
                                        <span>{billing.schedulePetName || billing.petName || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">정산일</span>
                                        <span>{formatDateTime(billing.createdAt)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* 이용한 활동 목록 */}
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold">이용한 활동</h4>
                                    {isEditMode && !showAddActivity && !showNewActivityForm && (
                                        <div className="flex items-center gap-2">
                                            {availableActivities.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAddActivity(true)}
                                                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                                                >
                                                    + 일정 활동 포함
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setShowNewActivityForm(true)}
                                                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                            >
                                                + 새 활동 추가
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {isEditMode ? (
                                    // 수정 모드
                                    <>
                                        {editActivityItems.length > 0 ? (
                                            <div className="space-y-1">
                                                {editActivityItems.map((item) => (
                                                    <div key={item.activityId} className="text-sm py-1">
                                                        {editingActivityTimeId === item.activityId ? (
                                                            <div className="p-2 bg-orange-50 rounded-lg border border-orange-200">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className="font-medium text-orange-700">{item.activityTypeName}</span>
                                                                    <span className="text-xs text-orange-600">(시간 수정 중)</span>
                                                                </div>
                                                                <div className="space-y-2 mb-2">
                                                                    <div className={cn("grid gap-2", editingActivityInstantComplete ? "grid-cols-1" : "grid-cols-2")}>
                                                                        <div>
                                                                            <label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1 block">시작</label>
                                                                            <input
                                                                                type="datetime-local"
                                                                                value={editingActivityStartAt}
                                                                                onChange={(e) => setEditingActivityStartAt(e.target.value)}
                                                                                className="w-full h-8 px-2 text-xs border rounded-md"
                                                                            />
                                                                        </div>
                                                                        {!editingActivityInstantComplete && (
                                                                            <div>
                                                                                <label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1 block">종료</label>
                                                                                <input
                                                                                    type="datetime-local"
                                                                                    value={editingActivityEndAt}
                                                                                    onChange={(e) => setEditingActivityEndAt(e.target.value)}
                                                                                    className="w-full h-8 px-2 text-xs border rounded-md"
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={editingActivityInstantComplete}
                                                                            onChange={(e) => {
                                                                                setEditingActivityInstantComplete(e.target.checked);
                                                                                if (e.target.checked) {
                                                                                    setEditingActivityEndAt('');
                                                                                }
                                                                            }}
                                                                            className="rounded border-neutral-300 dark:border-neutral-600 text-orange-500 focus:ring-orange-500"
                                                                        />
                                                                        <span className="text-xs text-neutral-600 dark:text-neutral-300">즉시종료 (체크시 종료일시 입력 불가)</span>
                                                                    </label>
                                                                </div>
                                                                <div className="flex justify-end gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={handleCancelEditActivityTime}
                                                                        className="h-7 px-3 text-xs"
                                                                        disabled={updateActivityMutation.isPending}
                                                                    >
                                                                        취소
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        onClick={handleSaveEditActivityTime}
                                                                        className="h-7 px-3 text-xs bg-orange-500 hover:bg-orange-600"
                                                                        disabled={updateActivityMutation.isPending}
                                                                    >
                                                                        {updateActivityMutation.isPending ? '저장 중...' : '저장'}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-between">
                                                                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={item.included}
                                                                        onChange={(e) => {
                                                                            setEditActivityItems(prev => prev.map(a =>
                                                                                a.activityId === item.activityId
                                                                                    ? { ...a, included: e.target.checked }
                                                                                    : a
                                                                            ));
                                                                        }}
                                                                        className="rounded border-neutral-300 dark:border-neutral-600 flex-shrink-0"
                                                                    />
                                                                    <div className="min-w-0">
                                                                        <span className={cn('truncate block', item.included ? '' : 'text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 line-through')}>
                                                                            {item.activityTypeName}
                                                                        </span>
                                                                        <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                                            {formatActivityTime(item.startAt)}
                                                                            {item.endAt && item.startAt && new Date(item.startAt).getTime() === new Date(item.endAt).getTime()
                                                                                ? ' (즉시종료)'
                                                                                : item.endAt ? ` ~ ${formatActivityTime(item.endAt)}` : ' (즉시종료)'}
                                                                        </span>
                                                                    </div>
                                                                </label>
                                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleStartEditActivityTime(item)}
                                                                        className="text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 hover:text-orange-500 p-1"
                                                                        title="시간 수정"
                                                                    >
                                                                        <Edit2 className="h-3 w-3" />
                                                                    </button>
                                                                    <Input
                                                                        type="number"
                                                                        value={item.amount}
                                                                        onChange={(e) => {
                                                                            setEditActivityItems(prev => prev.map(a =>
                                                                                a.activityId === item.activityId
                                                                                    ? { ...a, amount: parseInt(e.target.value) || 0 }
                                                                                    : a
                                                                            ));
                                                                        }}
                                                                        className="w-24 h-7 text-right text-sm"
                                                                        disabled={!item.included}
                                                                    />
                                                                    <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveActivity(item.activityId)}
                                                                        className="text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 hover:text-red-500 ml-1"
                                                                        title="활동 제거"
                                                                    >
                                                                        <X className="h-3 w-3" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium">
                                                    <span>활동 소계</span>
                                                    <span>{formatAmount(activityItemTotal)}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">활동 내역 없음</p>
                                        )}
                                        {/* 활동 추가 패널 */}
                                        {showAddActivity && (
                                            <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-medium text-orange-700">일정의 활동 중 추가할 항목을 선택하세요</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAddActivity(false)}
                                                        className="text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300"
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                                {availableActivities.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {availableActivities.map((activity) => (
                                                            <button
                                                                key={activity.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    handleAddActivity(activity);
                                                                    if (availableActivities.length <= 1) {
                                                                        setShowAddActivity(false);
                                                                    }
                                                                }}
                                                                className="w-full flex items-center justify-between p-2 bg-white dark:bg-neutral-800 rounded border border-neutral-200 dark:border-neutral-700 hover:border-orange-300 hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-colors text-left"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <Plus className="h-3.5 w-3.5 text-orange-500" />
                                                                    <span className="text-sm">{activity.activityTypeInfo?.name || '활동'}</span>
                                                                    <span className={cn(
                                                                        'text-[10px] px-1.5 py-0.5 rounded',
                                                                        activity.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-600' :
                                                                            activity.status === 'PROGRESS' ? 'bg-blue-100 text-blue-600' :
                                                                                'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 dark:text-neutral-500'
                                                                    )}>
                                                                        {activity.status === 'COMPLETED' ? '완료' :
                                                                            activity.status === 'PROGRESS' ? '진행중' :
                                                                                activity.status === 'CANCELED' ? '취소' : '대기'}
                                                                    </span>
                                                                </div>
                                                                <span className="text-sm text-neutral-600 dark:text-neutral-300 font-medium">
                                                                    {formatAmount(activity.activityTypeInfo?.defaultAmount || 0)}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 text-center py-2">추가 가능한 활동이 없습니다.</p>
                                                )}
                                            </div>
                                        )}
                                        {/* 새 활동 생성 폼 */}
                                        {showNewActivityForm && (
                                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium text-blue-700">새 활동 추가</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setShowNewActivityForm(false); resetNewActivityForm(); }}
                                                        className="text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300"
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>

                                                {/* 활동 유형 선택 */}
                                                <div>
                                                    <label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">활동 유형</label>
                                                    {newActivityTypeId ? (
                                                        <div className="mt-1 flex items-center gap-2">
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-300">
                                                                {newActivityTypeName}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setNewActivityTypeId(null); setNewActivityTypeName(''); setNewActivitySearch(''); }}
                                                                    className="ml-1 text-blue-500 hover:text-blue-700"
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </button>
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="mt-1 space-y-1.5">
                                                            <div className="relative">
                                                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500" />
                                                                <input
                                                                    type="text"
                                                                    value={newActivitySearch}
                                                                    onChange={(e) => setNewActivitySearch(e.target.value)}
                                                                    placeholder="활동 유형 검색..."
                                                                    className="w-full rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 dark:text-neutral-100 pl-7 pr-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                                                {filteredActivityTypes.length > 0 ? filteredActivityTypes.map((activityType) => (
                                                                    <button
                                                                        key={activityType.id}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewActivityTypeId(activityType.id);
                                                                            setNewActivityTypeName(activityType.name);
                                                                            setNewActivitySearch('');
                                                                        }}
                                                                        className="px-2 py-1 rounded-full text-xs font-medium border bg-white dark:bg-neutral-800 text-blue-700 border-blue-300 hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all"
                                                                    >
                                                                        {activityType.name}
                                                                    </button>
                                                                )) : (
                                                                    <p className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 py-1">
                                                                        {newActivitySearch ? '검색 결과가 없습니다.' : '등록된 활동 유형이 없습니다.'}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* 상태/시간 입력 (활동 유형 선택 후) */}
                                                {newActivityTypeId && (
                                                    <>
                                                        <div className={cn("grid gap-2", newActivityInstantComplete ? "grid-cols-2" : "grid-cols-3")}>
                                                            <div>
                                                                <label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1 block">상태</label>
                                                                <select
                                                                    value={newActivityStatus}
                                                                    onChange={(e) => setNewActivityStatus(e.target.value as any)}
                                                                    className="w-full h-8 px-2 text-xs border dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 dark:text-neutral-100"
                                                                >
                                                                    <option value="SCHEDULED">예정</option>
                                                                    <option value="PROGRESS">진행중</option>
                                                                    <option value="COMPLETED">완료</option>
                                                                    <option value="CANCELED">취소</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1 block">시작</label>
                                                                <input
                                                                    type="datetime-local"
                                                                    value={newActivityStartAt}
                                                                    onChange={(e) => setNewActivityStartAt(e.target.value)}
                                                                    className="w-full h-8 px-2 text-xs border rounded-md"
                                                                />
                                                            </div>
                                                            {!newActivityInstantComplete && (
                                                                <div>
                                                                    <label className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-1 block">종료</label>
                                                                    <input
                                                                        type="datetime-local"
                                                                        value={newActivityEndAt}
                                                                        onChange={(e) => setNewActivityEndAt(e.target.value)}
                                                                        className="w-full h-8 px-2 text-xs border rounded-md"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <label className="flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={newActivityInstantComplete}
                                                                onChange={(e) => {
                                                                    setNewActivityInstantComplete(e.target.checked);
                                                                    if (e.target.checked) setNewActivityEndAt('');
                                                                }}
                                                                className="rounded border-neutral-300 dark:border-neutral-600 text-blue-500 focus:ring-blue-500"
                                                            />
                                                            <span className="text-xs text-neutral-600 dark:text-neutral-300">즉시종료 (체크시 종료일시 입력 불가)</span>
                                                        </label>
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => { handleAddNewActivity(); setShowNewActivityForm(false); }}
                                                                className="flex-1 h-7 text-xs"
                                                            >
                                                                저장
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => { setShowNewActivityForm(false); resetNewActivityForm(); }}
                                                                className="flex-1 h-7 text-xs"
                                                            >
                                                                취소
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // 읽기 모드 - API에서 받은 activityItems 표시
                                    billing.activityItems && billing.activityItems.length > 0 ? (
                                        <div className="space-y-1">
                                            {billing.activityItems.map((item, idx) => {
                                                const matchedActivity = scheduleActivities.find(a => a.id === (item.petActivityId || item.id));
                                                return (
                                                    <div key={item.petActivityId || item.id || idx} className="flex justify-between text-sm py-1">
                                                        <div>
                                                            <span className={!item.included ? 'text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 line-through' : ''}>
                                                                {item.snapshotActivityTypeName || '활동'}
                                                            </span>
                                                            {matchedActivity && (
                                                                <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                                                    {formatActivityTime(matchedActivity.startAt)}
                                                                    {matchedActivity.endAt && matchedActivity.startAt && new Date(matchedActivity.startAt).getTime() === new Date(matchedActivity.endAt).getTime()
                                                                        ? ' (즉시종료)'
                                                                        : matchedActivity.endAt ? ` ~ ${formatActivityTime(matchedActivity.endAt)}` : ' (즉시종료)'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className={!item.included ? 'text-neutral-400 dark:text-neutral-500' : ''}>
                                                            {formatAmount(item.appliedAmount || 0)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium">
                                                <span>활동 소계</span>
                                                <span>{formatAmount(
                                                    billing.activityItems
                                                        .filter(i => i.included)
                                                        .reduce((sum, i) => sum + (i.appliedAmount || 0), 0)
                                                )}</span>
                                            </div>
                                        </div>
                                    ) : scheduleActivities.length > 0 ? (
                                        // API 데이터가 없으면 scheduleActivities로 폴백
                                        <div className="space-y-1">
                                            {scheduleActivities.map((activity) => (
                                                <div key={activity.id} className="flex justify-between text-sm py-1">
                                                    <div>
                                                        <span>{activity.activityTypeInfo?.name || '활동'}</span>
                                                        <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                                            {formatActivityTime(activity.startAt)}
                                                            {activity.endAt && activity.startAt && new Date(activity.startAt).getTime() === new Date(activity.endAt).getTime()
                                                                ? ' (즉시종료)'
                                                                : activity.endAt ? ` ~ ${formatActivityTime(activity.endAt)}` : ' (즉시종료)'}
                                                        </div>
                                                    </div>
                                                    <span>{formatAmount(activity.activityTypeInfo?.defaultAmount || 0)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">활동 내역 없음</p>
                                    )
                                )}
                            </div>

                            {/* 추가 결제 항목 (customItems) */}
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold">추가 결제 항목</h4>
                                    {isEditMode && !showAddItem && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAddItem(true)}
                                            className="text-xs text-blue-600 hover:text-blue-700"
                                        >
                                            + 항목 추가
                                        </button>
                                    )}
                                </div>
                                {isEditMode ? (
                                    // 수정 모드
                                    editAdditionalItems.length > 0 ? (
                                        <div className="space-y-1">
                                            {editAdditionalItems.map((item, idx) => (
                                                <div key={item.activityTypeId || idx} className="flex justify-between text-sm py-1">
                                                    <span>{item.activityTypeName}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span>{formatAmount(item.appliedAmount)}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveItem(item.activityTypeId)}
                                                            className="text-neutral-400 dark:text-neutral-500 hover:text-red-500"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium">
                                                <span>추가 항목 소계</span>
                                                <span>{formatAmount(additionalItemTotal)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-neutral-400 dark:text-neutral-500">추가 항목 없음</p>
                                    )
                                ) : (
                                    // 읽기 모드 - API customItems 표시
                                    billing.customItems && billing.customItems.length > 0 ? (
                                        <div className="space-y-1">
                                            {billing.customItems.map((item, idx) => (
                                                <div key={item.id || idx} className="flex justify-between text-sm py-1">
                                                    <span>
                                                        {item.name || '항목'}
                                                        {(item.quantity || 0) > 1 && <span className="text-neutral-400 dark:text-neutral-500 text-xs ml-1">x{item.quantity}</span>}
                                                    </span>
                                                    <span>{formatAmount((item.amount || 0) * (item.quantity || 1))}</span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium">
                                                <span>추가 항목 소계</span>
                                                <span>{formatAmount(
                                                    billing.customItems.reduce((sum, i) => sum + ((i.amount || 0) * (i.quantity || 1)), 0)
                                                )}</span>
                                            </div>
                                        </div>
                                    ) : editAdditionalItems.length > 0 ? (
                                        // API 데이터 없으면 editAdditionalItems로 폴백
                                        <div className="space-y-1">
                                            {editAdditionalItems.map((item, idx) => (
                                                <div key={item.activityTypeId || idx} className="flex justify-between text-sm py-1">
                                                    <span>{item.activityTypeName}</span>
                                                    <span>{formatAmount(item.appliedAmount)}</span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium">
                                                <span>추가 항목 소계</span>
                                                <span>{formatAmount(additionalItemTotal)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-neutral-400 dark:text-neutral-500">추가 항목 없음</p>
                                    )
                                )}
                                {isEditMode && showAddItem && (
                                    <div className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-800 rounded flex items-center gap-2">
                                        <Input
                                            value={newItemName}
                                            onChange={(e) => setNewItemName(e.target.value)}
                                            placeholder="항목명"
                                            className="flex-1 h-7 text-sm"
                                        />
                                        <Input
                                            type="number"
                                            value={newItemAmount}
                                            onChange={(e) => setNewItemAmount(e.target.value)}
                                            placeholder="금액"
                                            className="w-24 h-7 text-sm"
                                        />
                                        <Button size="sm" onClick={handleAddNewItem} className="h-7 px-2">추가</Button>
                                        <Button size="sm" variant="outline" onClick={() => { setShowAddItem(false); setNewItemName(''); setNewItemAmount(''); }} className="h-7 px-2">취소</Button>
                                    </div>
                                )}
                            </div>

                            {/* 할인 (discounts) */}
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold">할인</h4>
                                    {isEditMode && !showAddDiscount && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAddDiscount(true)}
                                            className="text-xs text-green-600 hover:text-green-700"
                                        >
                                            + 할인 추가
                                        </button>
                                    )}
                                </div>
                                {isEditMode ? (
                                    // 수정 모드
                                    discountItems.length > 0 ? (
                                        <div className="space-y-1">
                                            {discountItems.map((discount) => (
                                                <div key={discount.voucherId} className="flex justify-between text-sm py-1">
                                                    <span>{discount.voucherName}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-green-600">-{formatAmount(discount.discountAmount)}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveDiscount(discount.voucherId)}
                                                            className="text-neutral-400 dark:text-neutral-500 hover:text-red-500"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium text-green-600">
                                                <span>할인 소계</span>
                                                <span>-{formatAmount(totalDiscount)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-neutral-400 dark:text-neutral-500">할인 없음</p>
                                    )
                                ) : (
                                    // 읽기 모드 - API discounts 표시
                                    billing.discounts && billing.discounts.length > 0 ? (
                                        <div className="space-y-1">
                                            {billing.discounts.map((discount, idx) => (
                                                <div key={discount.id || idx} className="flex justify-between text-sm py-1">
                                                    <span>{discount.name || '할인'}</span>
                                                    <span className="text-green-600">-{formatAmount(discount.amount || 0)}</span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium text-green-600">
                                                <span>할인 소계</span>
                                                <span>-{formatAmount(
                                                    billing.discounts.reduce((sum, d) => sum + (d.amount || 0), 0)
                                                )}</span>
                                            </div>
                                        </div>
                                    ) : discountItems.length > 0 ? (
                                        // API 데이터 없으면 discountItems로 폴백
                                        <div className="space-y-1">
                                            {discountItems.map((discount) => (
                                                <div key={discount.voucherId} className="flex justify-between text-sm py-1">
                                                    <span>{discount.voucherName}</span>
                                                    <span className="text-green-600">-{formatAmount(discount.discountAmount)}</span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium text-green-600">
                                                <span>할인 소계</span>
                                                <span>-{formatAmount(totalDiscount)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-neutral-400 dark:text-neutral-500">할인 없음</p>
                                    )
                                )}
                                {isEditMode && showAddDiscount && (
                                    <div className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-800 rounded flex items-center gap-2">
                                        <Input
                                            value={newDiscountName}
                                            onChange={(e) => setNewDiscountName(e.target.value)}
                                            placeholder="할인명"
                                            className="flex-1 h-7 text-sm"
                                        />
                                        <Input
                                            type="number"
                                            value={newDiscountAmount}
                                            onChange={(e) => setNewDiscountAmount(e.target.value)}
                                            placeholder="금액"
                                            className="w-24 h-7 text-sm"
                                        />
                                        <Button size="sm" onClick={handleAddDiscount} className="h-7 px-2 bg-green-500 hover:bg-green-600">추가</Button>
                                        <Button size="sm" variant="outline" onClick={() => { setShowAddDiscount(false); setNewDiscountName(''); setNewDiscountAmount(''); }} className="h-7 px-2">취소</Button>
                                    </div>
                                )}
                            </div>

                            {/* 이용권 사용 내역 (voucherUsages) */}
                            {/* {billing.voucherUsages && billing.voucherUsages.length > 0 && (
                                <div className="p-4">
                                    <h4 className="text-sm font-semibold mb-2">이용권 사용</h4>
                                    <div className="space-y-1">
                                        {billing.voucherUsages.map((usage, idx) => (
                                            <div key={usage.memberVoucherId || usage.id || idx} className="flex justify-between text-sm py-1 bg-purple-50 px-2 rounded">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-purple-600 text-xs px-1.5 py-0.5 bg-purple-100 rounded">
                                                        {usage.snapshotType === 'HOURLY' ? '시간제' :
                                                            usage.snapshotType === 'PACKAGE' ? '횟수제' :
                                                                usage.snapshotType === 'PREPAID' ? '선불제' : '이용권'}
                                                    </span>
                                                    <span>{usage.snapshotVoucherName || '이용권'}</span>
                                                    {usage.usedCount && usage.usedCount > 1 && (
                                                        <span className="text-neutral-400 dark:text-neutral-500 text-xs">x{usage.usedCount}</span>
                                                    )}
                                                </div>
                                                <span className="text-purple-600 font-medium">-{formatAmount(usage.settlementAmount || 0)}</span>
                                            </div>
                                        ))}
                                        <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium text-purple-600">
                                            <span>이용권 사용 소계</span>
                                            <span>-{formatAmount(
                                                billing.voucherUsages.reduce((sum, v) => sum + (v.settlementAmount || 0), 0)
                                            )}</span>
                                        </div>
                                    </div>
                                </div>
                            )} */}

                            {/* 이용권 차감 */}
                            {/* {(billing.subscriptionId || selectedSubscriptions.length > 0) && (
                                <div className="p-4">
                                    <h4 className="text-sm font-semibold mb-2">이용권 차감</h4>
                                    {isEditMode ? (
                                        <div className="space-y-2">
                                            {selectedSubscriptions.map((sel) => {
                                                const sub = memberSubscriptions.find(s => s.id === sel.subscriptionId);
                                                if (!sub) return null;
                                                return (
                                                    <div key={sel.subscriptionId} className="flex items-center justify-between text-sm py-1 bg-blue-50 px-2 rounded">
                                                        <span>{sub.pricingPlanName} ({sub.pricingPlanType === 'PACKAGE' ? `${sel.deductedCount}회` : '잔액'})</span>
                                                        <div className="flex items-center gap-1">
                                                            <Input
                                                                type="number"
                                                                value={sel.settlementAmount || ''}
                                                                onChange={(e) => handleUpdateSettlementAmount(sub.id, parseInt(e.target.value) || 0)}
                                                                className="w-24 h-7 text-right text-sm"
                                                                placeholder="0"
                                                            />
                                                            <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {totalVoucherSettlement > 0 && (
                                                <div className="flex justify-between text-sm pt-2 border-t border-dashed font-medium text-blue-600">
                                                    <span>이용권 차감 소계</span>
                                                    <span>-{formatAmount(totalVoucherSettlement)}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        billing.subscriptionName && (
                                            <div className="space-y-1 text-sm">
                                                <div className="flex justify-between">
                                                    <span>{billing.subscriptionName}</span>
                                                    <span>{billing.deductedCount ? `${billing.deductedCount}회 차감` : ''}</span>
                                                </div>
                                                {billing.voucherSettlementAmount && billing.voucherSettlementAmount > 0 && (
                                                    <div className="flex justify-between text-blue-600">
                                                        <span>차감금액</span>
                                                        <span>-{formatAmount(billing.voucherSettlementAmount)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </div>
                            )} */}

                            {/* 금액 계산 내역 */}
                            <div className="p-4 bg-neutral-50 dark:bg-neutral-800">
                                <h4 className="text-sm font-semibold mb-2">금액 계산</h4>
                                <table className="w-full text-sm">
                                    <tbody>
                                        <tr>
                                            <td className="py-1 text-neutral-600 dark:text-neutral-300">기본 요금</td>
                                            <td className="py-1 text-right">
                                                {isEditMode ? (
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Input
                                                            type="number"
                                                            value={editBaseAmount}
                                                            onChange={(e) => setEditBaseAmount(e.target.value)}
                                                            className="w-24 h-7 text-right text-sm"
                                                        />
                                                        <span className="text-xs">원</span>
                                                    </div>
                                                ) : (
                                                    formatAmount(billing.baseAmount || 0)
                                                )}
                                            </td>
                                        </tr>
                                        {activityItemTotal > 0 && (
                                            <tr>
                                                <td className="py-1 text-neutral-600 dark:text-neutral-300">+ 활동 금액</td>
                                                <td className="py-1 text-right">{formatAmount(activityItemTotal)}</td>
                                            </tr>
                                        )}
                                        {additionalItemTotal > 0 && (
                                            <tr>
                                                <td className="py-1 text-neutral-600 dark:text-neutral-300">+ 추가 항목</td>
                                                <td className="py-1 text-right">{formatAmount(additionalItemTotal)}</td>
                                            </tr>
                                        )}
                                        {totalDiscount > 0 && (
                                            <tr>
                                                <td className="py-1 text-green-600">- 할인</td>
                                                <td className="py-1 text-right text-green-600">{formatAmount(totalDiscount)}</td>
                                            </tr>
                                        )}
                                        {totalVoucherSettlement > 0 && (
                                            <tr>
                                                <td className="py-1 text-blue-600">- 이용권 차감</td>
                                                <td className="py-1 text-right text-blue-600">{formatAmount(totalVoucherSettlement)}</td>
                                            </tr>
                                        )}
                                        <tr className="border-t font-semibold">
                                            <td className="py-2">계산 금액</td>
                                            <td className="py-2 text-right">{formatAmount(autoCalculatedAmount)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* 청구 금액 */}
                            <div className="p-4 bg-orange-50">
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold">청구 금액</span>
                                    {isEditMode ? (
                                        <div className="flex items-center gap-1">
                                            <Input
                                                type="number"
                                                value={editFinalAmount}
                                                onChange={(e) => setEditFinalAmount(e.target.value)}
                                                className="w-32 h-9 text-right text-lg font-bold border-orange-300"
                                            />
                                            <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
                                        </div>
                                    ) : (
                                        <span className="text-xl font-bold text-orange-600">{formatAmount(billing.finalAmount || 0)}</span>
                                    )}
                                </div>
                                {isEditMode && parseInt(editFinalAmount) !== autoCalculatedAmount && (
                                    <p className="text-xs mt-1 text-orange-600">
                                        ※ 계산 금액과 {formatAmount(Math.abs(parseInt(editFinalAmount) - autoCalculatedAmount))} 차이
                                    </p>
                                )}
                            </div>

                            {/* 적용 이용권 (수정 모드에서만 표시) */}
                            {isEditMode && memberSubscriptions.length > 0 && (
                                <div className="p-4">
                                    <h4 className="text-sm font-semibold mb-2">적용할 이용권</h4>
                                    <div className="space-y-2">
                                        {/* 선택된 이용권 목록 */}
                                        {selectedSubscriptions.length > 0 && (
                                            <div className="space-y-2 mb-3">
                                                {selectedSubscriptions.map((sel) => {
                                                    const sub = memberSubscriptions.find(s => s.id === sel.subscriptionId);
                                                    if (!sub) return null;

                                                    return (
                                                        <div key={sub.id} className="p-3 rounded-lg border border-orange-500 bg-orange-50">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    {sub.pricingPlanType === 'PACKAGE' && (
                                                                        <Package className="h-4 w-4 text-blue-500" />
                                                                    )}
                                                                    {sub.pricingPlanType === 'PREPAID' && (
                                                                        <CreditCard className="h-4 w-4 text-purple-500" />
                                                                    )}
                                                                    <span className="font-medium text-sm">{sub.pricingPlanName}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveSubscription(sub.id)}
                                                                    className="p-1 text-neutral-400 dark:text-neutral-500 hover:text-red-500"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </button>
                                                            </div>

                                                            {/* 횟수제: 차감 횟수 선택 */}
                                                            {sub.pricingPlanType === 'PACKAGE' && (
                                                                <div className="mt-2 pt-2 border-t border-orange-200">
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-xs text-neutral-600 dark:text-neutral-300">차감 횟수</span>
                                                                        <div className="flex items-center gap-2">
                                                                            <select
                                                                                value={sel.deductedCount}
                                                                                onChange={(e) => handleUpdateDeductedCount(sub.id, parseInt(e.target.value) || 1)}
                                                                                className="h-7 px-2 border rounded-md text-xs bg-white dark:bg-neutral-900"
                                                                            >
                                                                                {Array.from({ length: sub.remainingCount || 1 }, (_, i) => i + 1).map((count) => (
                                                                                    <option key={count} value={count}>
                                                                                        {count}회
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                            <span className="text-xs text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                                                                                / {sub.remainingCount}회
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-xs text-blue-500 mt-1">
                                                                        → 차감 후 잔여: {(sub.remainingCount || 0) - sel.deductedCount}회
                                                                    </p>
                                                                </div>
                                                            )}

                                                            {/* 선불제: 잔액 표시 */}
                                                            {sub.pricingPlanType === 'PREPAID' && (
                                                                <div className="mt-2 pt-2 border-t border-orange-200">
                                                                    <div className="flex items-center justify-between text-xs">
                                                                        <span className="text-neutral-600 dark:text-neutral-300">현재 잔액</span>
                                                                        <span className="text-purple-600 font-medium">
                                                                            {formatAmount(sub.remainingAmount || 0)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* 정산금 입력 */}
                                                            <div className="mt-2 pt-2 border-t border-orange-200">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs text-blue-700 font-medium">
                                                                        {sub.pricingPlanType === 'PACKAGE' ? '횟수 정산금' : '선불 정산금'}
                                                                    </span>
                                                                    <div className="flex items-center gap-1">
                                                                        <Input
                                                                            type="number"
                                                                            min={0}
                                                                            max={sub.pricingPlanType === 'PREPAID' ? sub.remainingAmount : undefined}
                                                                            value={sel.settlementAmount || ''}
                                                                            onChange={(e) => {
                                                                                let value = parseInt(e.target.value) || 0;
                                                                                if (sub.pricingPlanType === 'PREPAID' && sub.remainingAmount) {
                                                                                    value = Math.min(value, sub.remainingAmount);
                                                                                    value = Math.max(value, 0);
                                                                                }
                                                                                handleUpdateSettlementAmount(sub.id, value);
                                                                            }}
                                                                            className="w-24 h-7 text-right text-sm"
                                                                            placeholder="0"
                                                                        />
                                                                        <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
                                                                    </div>
                                                                </div>
                                                                <p className="text-xs text-blue-500 mt-1">
                                                                    * 이용권에서 차감되는 금액 (청구 금액에서 제외)
                                                                </p>
                                                                {sub.pricingPlanType === 'PREPAID' && (
                                                                    <p className="text-xs text-purple-600 mt-1">
                                                                        최대 입력 가능: {formatAmount(sub.remainingAmount || 0)} (현재 잔액)
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* 추가 가능한 이용권 목록 */}
                                        {memberSubscriptions.filter(sub => !selectedSubscriptions.some(s => s.subscriptionId === sub.id)).length > 0 && (
                                            <div>
                                                <p className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mb-2">이용권 추가:</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {memberSubscriptions
                                                        .filter(sub => !selectedSubscriptions.some(s => s.subscriptionId === sub.id))
                                                        .map((sub) => (
                                                            <button
                                                                key={sub.id}
                                                                type="button"
                                                                onClick={() => handleAddSubscription(sub.id)}
                                                                className="px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-orange-400 hover:bg-orange-50 transition-all text-left"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <Plus className="h-3 w-3 text-orange-500" />
                                                                    {sub.pricingPlanType === 'PACKAGE' && (
                                                                        <Package className="h-3.5 w-3.5 text-blue-500" />
                                                                    )}
                                                                    {sub.pricingPlanType === 'PREPAID' && (
                                                                        <CreditCard className="h-3.5 w-3.5 text-purple-500" />
                                                                    )}
                                                                    <span className="font-medium text-xs">{sub.pricingPlanName}</span>
                                                                </div>
                                                                <div className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500 mt-0.5 ml-5">
                                                                    {sub.pricingPlanType === 'PACKAGE' && (
                                                                        <span>잔여: {sub.remainingCount}회</span>
                                                                    )}
                                                                    {sub.pricingPlanType === 'PREPAID' && (
                                                                        <span>잔액: {formatAmount(sub.remainingAmount || 0)}</span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 부분결제/환불 내역 */}
                            <div className="p-4">
                                <h4 className="text-sm font-semibold mb-2">결제/환불 내역</h4>
                                {isEditMode ? (
                                    // 수정 모드 - 편집 가능한 결제/환불 내역
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                                            className="w-full flex items-center justify-between text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800 dark:bg-neutral-800 p-2 rounded border"
                                        >
                                            <span className="flex items-center gap-2">
                                                결제 {partialPayments.length}건 / 환불 {partialRefunds.length}건
                                                {(partialPayments.length > 0 || partialRefunds.length > 0) && (
                                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                        {partialPayments.length + partialRefunds.length}
                                                    </span>
                                                )}
                                            </span>
                                            {showPaymentHistory ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            )}
                                        </button>

                                        {showPaymentHistory && (
                                            <div className="mt-3 space-y-4 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                                                {/* 결제 내역 - 수정 모드 */}
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">결제 내역</span>
                                                        <span className="text-xs text-green-600 font-medium">
                                                            총 {formatAmount(totalPaidAmount)}
                                                        </span>
                                                    </div>

                                                    {partialPayments.length > 0 && (
                                                        <div className="space-y-1">
                                                            {partialPayments.map((payment) => (
                                                                <div
                                                                    key={payment.id}
                                                                    className="flex items-center justify-between p-2 bg-green-50 rounded text-xs"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <CreditCard className="h-3 w-3 text-green-600" />
                                                                        <span>{getPaymentMethodLabel(payment.paymentMethod)}</span>
                                                                        {payment.memo && (
                                                                            <span className="text-neutral-400 dark:text-neutral-500">({payment.memo})</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-green-700 font-medium">
                                                                            +{formatAmount(payment.amount)}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemovePartialPayment(payment.id)}
                                                                            className="text-neutral-400 dark:text-neutral-500 hover:text-red-500"
                                                                        >
                                                                            <X className="h-3 w-3" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 부분결제 추가 - 수정 모드에서만 */}
                                                    {showAddPayment ? (
                                                        <div className="space-y-2 p-3 bg-white dark:bg-neutral-900 rounded-lg border">
                                                            <div className="flex items-center gap-2">
                                                                <Input
                                                                    type="number"
                                                                    value={newPaymentAmount}
                                                                    onChange={(e) => setNewPaymentAmount(e.target.value)}
                                                                    placeholder="결제 금액"
                                                                    className="flex-1 h-8 text-sm"
                                                                />
                                                                <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
                                                            </div>
                                                            <div className="grid grid-cols-4 gap-1">
                                                                {PAYMENT_METHOD_OPTIONS.map((option) => (
                                                                    <button
                                                                        key={option.value}
                                                                        type="button"
                                                                        onClick={() => setNewPaymentMethod(option.value)}
                                                                        className={cn(
                                                                            "p-1.5 rounded text-xs border",
                                                                            newPaymentMethod === option.value
                                                                                ? "border-green-500 bg-green-50 text-green-700"
                                                                                : "border-neutral-200 dark:border-neutral-700"
                                                                        )}
                                                                    >
                                                                        {option.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <Input
                                                                value={newPaymentMemo}
                                                                onChange={(e) => setNewPaymentMemo(e.target.value)}
                                                                placeholder={newPaymentMethod === 'OTHER' ? "기타 상세 내용 (필수)" : "메모 (선택)"}
                                                                className="h-8 text-sm"
                                                            />
                                                            <div className="flex gap-2">
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    variant="outline"
                                                                    onClick={() => setShowAddPayment(false)}
                                                                    className="flex-1 h-7"
                                                                >
                                                                    취소
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    onClick={handleAddPartialPayment}
                                                                    className="flex-1 h-7 bg-green-500 hover:bg-green-600"
                                                                >
                                                                    결제 등록
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : remainingPaymentAmount > 0 ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAddPayment(true)}
                                                            className="w-full p-2 border border-dashed border-green-300 rounded-lg text-xs text-green-600 hover:border-green-400 hover:bg-green-50 transition-colors flex items-center justify-center gap-1"
                                                        >
                                                            <Plus className="h-3 w-3" />
                                                            부분결제 추가 (잔액: {formatAmount(remainingPaymentAmount)})
                                                        </button>
                                                    ) : (
                                                        <div className="text-xs text-center text-green-600 py-2">
                                                            <Check className="h-4 w-4 inline mr-1" />
                                                            전액 결제 완료
                                                        </div>
                                                    )}
                                                </div>

                                                {/* 환불 내역 - 수정 모드 */}
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">환불 내역</span>
                                                        <span className="text-xs text-red-600 font-medium">
                                                            총 {formatAmount(totalRefundedAmount)}
                                                        </span>
                                                    </div>

                                                    {partialRefunds.length > 0 && (
                                                        <div className="space-y-1">
                                                            {partialRefunds.map((refund) => (
                                                                <div
                                                                    key={refund.id}
                                                                    className="flex items-center justify-between p-2 bg-red-50 rounded text-xs"
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <RotateCcw className="h-3 w-3 text-red-600" />
                                                                        <span className="text-neutral-600 dark:text-neutral-300">{refund.reason}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-red-700 font-medium">
                                                                            -{formatAmount(refund.amount)}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemovePartialRefund(refund.id)}
                                                                            className="text-neutral-400 dark:text-neutral-500 hover:text-red-500"
                                                                        >
                                                                            <X className="h-3 w-3" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 부분환불 추가 - 수정 모드에서만 */}
                                                    {showAddRefund ? (
                                                        <div className="space-y-2 p-3 bg-white dark:bg-neutral-900 rounded-lg border">
                                                            <div className="flex items-center gap-2">
                                                                <Input
                                                                    type="number"
                                                                    value={newRefundAmount}
                                                                    onChange={(e) => setNewRefundAmount(e.target.value)}
                                                                    placeholder="환불 금액"
                                                                    className="flex-1 h-8 text-sm"
                                                                />
                                                                <span className="text-xs text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">원</span>
                                                            </div>
                                                            <div className="grid grid-cols-4 gap-1">
                                                                {PAYMENT_METHOD_OPTIONS.map((option) => (
                                                                    <button
                                                                        key={option.value}
                                                                        type="button"
                                                                        onClick={() => setNewRefundMethod(option.value)}
                                                                        className={cn(
                                                                            "p-1.5 rounded text-xs border",
                                                                            newRefundMethod === option.value
                                                                                ? "border-red-500 bg-red-50 text-red-700"
                                                                                : "border-neutral-200 dark:border-neutral-700"
                                                                        )}
                                                                    >
                                                                        {option.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <Input
                                                                value={newRefundReason}
                                                                onChange={(e) => setNewRefundReason(e.target.value)}
                                                                placeholder={newRefundMethod === 'OTHER' ? "환불 사유 및 기타 상세 내용 (필수)" : "환불 사유"}
                                                                className="h-8 text-sm"
                                                            />
                                                            <div className="flex gap-2">
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    variant="outline"
                                                                    onClick={() => setShowAddRefund(false)}
                                                                    className="flex-1 h-7"
                                                                >
                                                                    취소
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    onClick={handleAddPartialRefund}
                                                                    className="flex-1 h-7 bg-red-500 hover:bg-red-600"
                                                                >
                                                                    환불 등록
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : refundableAmount > 0 ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAddRefund(true)}
                                                            className="w-full p-2 border border-dashed border-red-300 rounded-lg text-xs text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
                                                        >
                                                            <RotateCcw className="h-3 w-3" />
                                                            부분환불 추가 (가능: {formatAmount(refundableAmount)})
                                                        </button>
                                                    ) : null}
                                                </div>

                                                {/* 정산 요약 */}
                                                {(partialPayments.length > 0 || partialRefunds.length > 0) && (
                                                    <div className="pt-3 border-t space-y-1">
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">총 결제</span>
                                                            <span className="text-green-600">+{formatAmount(totalPaidAmount)}</span>
                                                        </div>
                                                        <div className="flex justify-between text-xs">
                                                            <span className="text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">총 환불</span>
                                                            <span className="text-red-600">-{formatAmount(totalRefundedAmount)}</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm font-semibold pt-1 border-t">
                                                            <span>실 결제액</span>
                                                            <span className="text-blue-600">
                                                                {formatAmount(totalPaidAmount - totalRefundedAmount)}
                                                            </span>
                                                        </div>
                                                        {remainingPaymentAmount > 0 && (
                                                            <div className="flex justify-between text-xs text-orange-600">
                                                                <span>미결제</span>
                                                                <span>{formatAmount(remainingPaymentAmount)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // 읽기 모드 - API 데이터 표시 (추가/삭제 불가)
                                    <div className="space-y-3">
                                        {/* API payments 표시 */}
                                        {billing.payments && billing.payments.length > 0 ? (
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-medium">
                                                    <span>결제 내역</span>
                                                    <span className="text-green-600">+{formatAmount(billing.totalPaidAmount || 0)}</span>
                                                </div>
                                                {billing.payments.map((payment, idx) => (
                                                    <div key={payment.id || idx} className="flex justify-between text-xs p-2 bg-green-50 rounded">
                                                        <div className="flex items-center gap-2">
                                                            <CreditCard className="h-3 w-3 text-green-600" />
                                                            <span>{getPaymentMethodLabel(payment.method || 'CASH')}</span>
                                                            {payment.paidAt && <span className="text-neutral-400 dark:text-neutral-500 text-xs">({new Date(payment.paidAt).toLocaleDateString()})</span>}
                                                        </div>
                                                        <span className="text-green-700 font-medium">+{formatAmount(payment.amount || 0)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : billing.partialPayments && billing.partialPayments.length > 0 ? (
                                            // API payments가 없으면 partialPayments로 폴백
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-medium">
                                                    <span>결제 내역</span>
                                                    <span className="text-green-600">+{formatAmount(billing.totalPaidAmount || 0)}</span>
                                                </div>
                                                {billing.partialPayments.map((payment, idx) => (
                                                    <div key={payment.id || idx} className="flex justify-between text-xs p-2 bg-green-50 rounded">
                                                        <div className="flex items-center gap-2">
                                                            <CreditCard className="h-3 w-3 text-green-600" />
                                                            <span>{getPaymentMethodLabel(payment.paymentMethod)}</span>
                                                            {payment.memo && <span className="text-neutral-400 dark:text-neutral-500">({payment.memo})</span>}
                                                        </div>
                                                        <span className="text-green-700 font-medium">+{formatAmount(payment.amount)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-neutral-400 dark:text-neutral-500">결제 내역 없음</p>
                                        )}

                                        {/* API cashRefunds 표시 (BillingCashRefundResponse uses 'method' and 'amount') */}
                                        {billing.cashRefunds && billing.cashRefunds.length > 0 ? (
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-medium">
                                                    <span>환불 내역</span>
                                                    <span className="text-red-600">-{formatAmount(billing.totalRefundedAmount || 0)}</span>
                                                </div>
                                                {billing.cashRefunds.map((refund, idx) => (
                                                    <div key={refund.id || idx} className="flex justify-between text-xs p-2 bg-red-50 rounded">
                                                        <div className="flex items-center gap-2">
                                                            <RotateCcw className="h-3 w-3 text-red-600" />
                                                            <span>{getPaymentMethodLabel(refund.method || 'CASH')}</span>
                                                            {refund.reason && <span className="text-neutral-400 dark:text-neutral-500">({refund.reason})</span>}
                                                        </div>
                                                        <span className="text-red-700 font-medium">-{formatAmount(refund.amount || 0)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : billing.partialRefunds && billing.partialRefunds.length > 0 ? (
                                            // API cashRefunds가 없으면 partialRefunds로 폴백
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-medium">
                                                    <span>환불 내역</span>
                                                    <span className="text-red-600">-{formatAmount(billing.totalRefundedAmount || 0)}</span>
                                                </div>
                                                {billing.partialRefunds.map((refund, idx) => (
                                                    <div key={refund.id || idx} className="flex justify-between text-xs p-2 bg-red-50 rounded">
                                                        <div className="flex items-center gap-2">
                                                            <RotateCcw className="h-3 w-3 text-red-600" />
                                                            <span>{getPaymentMethodLabel(refund.refundMethod)}</span>
                                                            {refund.reason && <span className="text-neutral-400 dark:text-neutral-500">({refund.reason})</span>}
                                                        </div>
                                                        <span className="text-red-700 font-medium">-{formatAmount(refund.amount)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}

                                        {/* 요약 */}
                                        {((!!billing.payments && billing.payments.length > 0) ||
                                            (billing.cashRefunds && billing.cashRefunds.length > 0) ||
                                            (billing.partialPayments && billing.partialPayments.length > 0) ||
                                            (billing.partialRefunds && billing.partialRefunds.length > 0)) && (
                                                <div className="flex justify-between text-sm font-semibold pt-3 border-t">
                                                    <span>실 결제액</span>
                                                    <span className="text-blue-600">
                                                        {formatAmount((billing.totalPaidAmount || 0) - (billing.totalRefundedAmount || 0))}
                                                    </span>
                                                </div>
                                            )}

                                        {!!billing.remainingAmount && billing.remainingAmount > 0 && (
                                            <div className="flex justify-between text-xs text-orange-600">
                                                <span>미결제</span>
                                                <span>{formatAmount(billing.remainingAmount)}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* 메모 */}
                            <div className="p-4">
                                <h4 className="text-sm font-semibold mb-2">메모</h4>
                                {isEditMode ? (
                                    <textarea
                                        value={editMemo}
                                        onChange={(e) => setEditMemo(e.target.value)}
                                        placeholder="정산 관련 메모를 입력하세요..."
                                        className="w-full h-16 p-2 border border-neutral-200 dark:border-neutral-700 rounded text-sm resize-none"
                                    />
                                ) : (
                                    <p className="text-sm text-neutral-600 dark:text-neutral-300">{billing.memo || '-'}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* 푸터 */}
                <div className="sticky bottom-0 bg-white dark:bg-neutral-900 border-t p-4 z-10">
                    {isEditMode ? (
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={handleCancelEdit} className="flex-1">
                                취소
                            </Button>
                            <Button onClick={handleSaveEdit} className="flex-1 bg-orange-500 hover:bg-orange-600">
                                저장
                            </Button>
                        </div>
                    ) : (
                        <Button onClick={onClose} className="w-full">
                            닫기
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
